/**
 * Order-detail presentational view (§18 /queue/:orderId, Task 2 hierarchy). Pure
 * render of an OrderDetail, ordered by what an operator decides on:
 *   1. blockers loud first — exception banner, then a prominent READINESS callout
 *      when the order isn't ready (it's what stops it shipping);
 *   2. a decision-facts strip (destination · value · weight · items);
 *   3. fulfilment (carrier + service), customs (§Task4, non-EU only), ship-to;
 *   4. line items with product images (§Task3);
 *   5. the routing-rule trace (§8.5) last + de-emphasized — it's diagnostic.
 * Parsing stays in lib/orderTrace; humanizers keep raw codes/ids off-screen.
 */
import { useMemo } from "react";
import type { OrderDetail } from "../../lib/api.js";
import { parseRoutingTrace, parseReadinessReasons } from "../../lib/orderTrace.js";
import {
  gramsToKg,
  formatMoney,
  humanizeCode,
  humanizeServiceCode,
  humanizeRuleName,
  humanizeReadinessReason,
  storeName,
} from "../../lib/format.js";
import { countryName } from "../../lib/countries.js";
import { StatusChip, Tag, Icon, Thumb, CarrierMark } from "../../components/ui/index.js";
import type { IconName } from "../../components/ui/Icon.js";

/** Section header: a solid category-coloured icon chip + label (the chip + the
 *  card's coloured spine share the section's --od-cat) — a strong scannable
 *  anchor so the eye finds carrier / customs / address / items down the drawer. */
function SectionTitle({ icon, children }: { icon: IconName; children: React.ReactNode }) {
  return (
    <h2 className="od-section__title">
      <span className="od-section__icon">
        <Icon name={icon} size={14} />
      </span>
      <span className="od-section__label">{children}</span>
    </h2>
  );
}

/** Carrier shown to operators is humanized (DHL_EXPRESS → "DHL Express"). */
function carrierLabel(order: OrderDetail["order"]): string {
  if (!order.carrierCode) return "Unrouted";
  return `${humanizeCode(order.carrierCode)}${order.serviceCode ? ` · ${humanizeServiceCode(order.serviceCode)}` : ""}`;
}

function addressLines(a: OrderDetail["address"]): string[] {
  if (!a) return [];
  const country = a.country ?? a.countryCode ?? null;
  return [
    a.name,
    a.company,
    a.address1,
    a.address2,
    [a.zip, a.city].filter(Boolean).join(" ") || null,
    [a.province, country].filter(Boolean).join(", ") || null,
    a.phone,
  ].filter((l): l is string => !!l && l.trim() !== "");
}

type IorParty = NonNullable<NonNullable<OrderDetail["order"]["importerOfRecord"]>["party"]>;

function iorAddressLines(a: IorParty["address"], entityName: string): string[] {
  const country = a.country ? countryName(a.country) || a.country : null;
  return [
    // Contact / attention line + company, each only when distinct from the entity
    // heading (so we don't repeat the legal name the card already shows on top).
    a.name && a.name !== entityName ? a.name : null,
    a.company && a.company !== entityName ? a.company : null,
    a.street1,
    a.street2,
    [a.postcode, a.city].filter(Boolean).join(" ") || null,
    [a.province, country].filter(Boolean).join(", ") || null,
  ].filter((l): l is string => !!l && l.trim() !== "");
}

/** §10.2 — the distinct importing entity (≠ store ship-from, ≠ recipient). */
function IorEntity({ party }: { party: IorParty }) {
  const regs: Array<[string, string]> = [];
  if (party.eori) regs.push(["EORI", party.eori]);
  if (party.vatNumber) regs.push(["VAT", party.vatNumber]);
  if (party.ein) regs.push(["EIN", party.ein]);
  if (party.craNumber) regs.push(["CRA", party.craNumber]);
  for (const [k, v] of Object.entries(party.importerIds ?? {})) if (v) regs.push([k, v]);
  const lines = iorAddressLines(party.address, party.name);
  return (
    <div className="od-ior">
      <div className="od-ior__name">{party.name}</div>
      {lines.length > 0 && (
        <address className="od-address">
          {lines.map((l, i) => (
            <div key={i}>{l}</div>
          ))}
        </address>
      )}
      {(party.address.phone || party.address.email) && (
        <div className="od-ior__contact muted">
          {party.address.phone && <span>{party.address.phone}</span>}
          {party.address.email && <span>{party.address.email}</span>}
        </div>
      )}
      {regs.length > 0 && (
        <div className="od-ior__regs">
          {regs.map(([k, v]) => (
            <Tag key={k}>{k} {v}</Tag>
          ))}
        </div>
      )}
    </div>
  );
}

/** Drawer header title: order # + status chip. */
export function OrderTitle({ order }: { order: OrderDetail["order"] }) {
  return (
    <div className="od-title">
      <h1>{order.name}</h1>
      <StatusChip status={order.status} />
    </div>
  );
}

/** Drawer header meta: the decision-at-a-glance line (store · dest · value) + route. */
export function OrderMeta({ order }: { order: OrderDetail["order"] }) {
  return (
    <>
      <div className="od-meta">
        <span>{storeName(order)}</span>
        <span className="od-meta__sep">·</span>
        <span title={order.destinationCountry ?? undefined}>{countryName(order.destinationCountry) || "—"}</span>
        <span className="od-meta__sep">·</span>
        <span className="tnum">{formatMoney(order.totalPrice, order.currency)}</span>
      </div>
      <div className="od-meta od-meta--route">
        <CarrierMark code={order.carrierCode} showName />
        {order.serviceCode && <span className="muted">· {humanizeServiceCode(order.serviceCode)}</span>}
      </div>
    </>
  );
}

export function OrderDetailBody({ detail }: { detail: OrderDetail }) {
  const events = detail.events;
  const order = detail.order;
  const routing = useMemo(() => parseRoutingTrace(events), [events]);
  const readiness = useMemo(() => parseReadinessReasons(events), [events]);
  const blocked = readiness.length > 0;
  const weight = order.theoreticalWeightG ?? order.itemsWeightG ?? null;
  const itemCount = detail.lines.reduce((n, l) => n + l.quantity, 0);

  return (
    <div className="od-body">
      {/* 1 — blockers, loud + first. */}
      {order.exceptionReason && (
        <p className="od-exception">
          <Icon name="alert" size={15} />
          {humanizeCode(order.exceptionReason)}
        </p>
      )}

      {blocked ? (
        <section className="od-callout od-callout--block" aria-label="Readiness">
          <div className="od-callout__head">
            <Icon name="alert" size={16} />
            <span>Not ready to ship — {readiness.length} {readiness.length === 1 ? "blocker" : "blockers"}</span>
          </div>
          <ul className="readiness-list">
            {readiness.map((r, i) => (
              <li key={`${r.ruleId}-${i}`} className={`readiness-item readiness-item--${r.effect}`}>
                <Icon name={r.effect === "BLOCK" ? "alert" : "dot"} size={r.effect === "BLOCK" ? 15 : 6} />
                <span>{humanizeReadinessReason(r.effect, r.message)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : (
        !order.exceptionReason && (
          <p className="od-ready">
            <Icon name="check" size={15} /> Ready to pack
          </p>
        )
      )}

      {/* 2 — decision facts. */}
      <div className="od-facts">
        <div className="od-fact">
          <span className="od-fact__k">Destination</span>
          <span className="od-fact__v">{countryName(order.destinationCountry) || "—"}</span>
        </div>
        <div className="od-fact">
          <span className="od-fact__k">Value</span>
          <span className="od-fact__v tnum">{formatMoney(order.totalPrice, order.currency)}</span>
        </div>
        <div className="od-fact">
          <span className="od-fact__k">Weight</span>
          <span className="od-fact__v tnum">{weight != null ? `${gramsToKg(weight)} kg` : "—"}</span>
        </div>
        <div className="od-fact">
          <span className="od-fact__k">Items</span>
          <span className="od-fact__v tnum">{itemCount}</span>
        </div>
      </div>

      {/* 3 — fulfilment (carrier + service). */}
      <section className="od-section od-section--carrier">
        <SectionTitle icon="truck">Carrier &amp; service</SectionTitle>
        <p className="od-fulfil">
          <CarrierMark code={order.carrierCode} size={28} />
          <span className="od-fulfil__v">{carrierLabel(order)}</span>
          {order.shippingMethodTitle && <span className="muted">· {order.shippingMethodTitle}</span>}
        </p>
      </section>

      {/* 4 — customs (§Task4) — non-EU only. */}
      {order.customs && (
        <section className="od-section od-section--customs">
          <SectionTitle icon="globe">Customs</SectionTitle>
          <div className="od-customs">
            <div className="od-customs__item">
              <span className="od-customs__k">Incoterm</span>
              <span className="od-customs__v">{order.customs.incoterm ?? <span className="muted">no trade lane configured</span>}</span>
            </div>
            <div className="od-customs__item">
              <span className="od-customs__k">Customs value</span>
              <span className="od-customs__v tnum">
                {order.customs.customsValueTotal != null ? formatMoney(order.customs.customsValueTotal, order.customs.customsCurrency) : "—"}
                {order.customs.customsValueTotal != null && !order.customs.complete && <span className="muted"> · partial</span>}
              </span>
            </div>
            {order.customs.valueBasis && (
              <div className="od-customs__item">
                <span className="od-customs__k">Basis</span>
                <span className="od-customs__v">{humanizeCode(order.customs.valueBasis)}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 4b — importer of record (§10.2). ENTITY → distinct party shown in full;
          CUSTOMER → the recipient is the importer (note). */}
      {order.importerOfRecord && (
        <section className="od-section od-section--importer">
          <SectionTitle icon="users">Importer of record</SectionTitle>
          {order.importerOfRecord.type === "ENTITY" && order.importerOfRecord.party ? (
            <IorEntity party={order.importerOfRecord.party} />
          ) : (
            <p className="od-ior-note">
              <Icon name="users" size={15} /> The recipient is the importer of record — the customer clears customs.
            </p>
          )}
        </section>
      )}

      {/* 5 — ship to. */}
      <section className="od-section od-section--shipto">
        <SectionTitle icon="pin">Ship to</SectionTitle>
        {detail.address && addressLines(detail.address).length > 0 ? (
          <address className="od-address">
            {addressLines(detail.address).map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </address>
        ) : (
          <p className="muted">No address on file.</p>
        )}
      </section>

      {/* 6 — line items (with product images §Task3). */}
      <section className="od-section od-section--items">
        <SectionTitle icon="box">Items ({detail.lines.length})</SectionTitle>
        <ul className="od-lines">
          {detail.lines.map((l, i) => (
            <li key={`${l.sku ?? "nosku"}-${i}`} className="od-line">
              <Thumb src={l.imageUrl} alt={l.title} size={44} />
              <div className="od-line__body">
                <div className="od-line__main">
                  <span className="od-line__qty tnum">{l.quantity}×</span>
                  <span className="od-line__title">
                    {l.title}
                    {l.variantTitle && <span className="muted"> — {l.variantTitle}</span>}
                  </span>
                </div>
                <div className="od-line__sub">
                  <span>{l.sku ?? "no SKU"}</span>
                  {l.barcode && <span>· {l.barcode}</span>}
                  {l.unitWeightG != null && <span>· {gramsToKg(l.unitWeightG)} kg</span>}
                  {l.requiresCustoms && (
                    <>
                      <Tag tone="warn">customs</Tag>
                      {l.hsCode && <span>HS {l.hsCode}</span>}
                      {l.countryOfOrigin && <span>{countryName(l.countryOfOrigin) || l.countryOfOrigin}</span>}
                    </>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* 7 — shipments (read-only). */}
      {detail.shipments.length > 0 && (
        <section className="od-section od-section--shipments">
          <SectionTitle icon="truck">Shipments</SectionTitle>
          <ul className="od-ships">
            {detail.shipments.map((s, i) => (
              <li key={`${s.trackingNumber ?? "no-track"}-${i}`} className="od-ship">
                <CarrierMark code={s.carrierCode} showName />
                <span className="muted" title={s.serviceCode}>{humanizeServiceCode(s.serviceCode)}</span>
                <StatusChip status={s.status} />
                {s.trackingNumber ? <span className="od-ship__track">{s.trackingNumber}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* 8 — routing trace (§8.5) — diagnostic, de-emphasized + last. */}
      <section className="od-section od-section--muted od-section--routing">
        <SectionTitle icon="route">Routing trace</SectionTitle>
        {routing ? (
          <>
            <p className="od-routing-summary">
              {routing.matchedRuleId || routing.matchedRuleName ? (
                <>
                  Matched <span title={routing.matchedRuleId ?? undefined}>{humanizeRuleName(routing.matchedRuleName ?? routing.matchedRuleId)}</span>
                  {" → "}
                  {routing.carrierCode ? humanizeCode(routing.carrierCode) : "—"}
                  {routing.serviceCode ? ` · ${humanizeServiceCode(routing.serviceCode)}` : ""}
                </>
              ) : routing.exceptionReason ? (
                `No rule matched — ${humanizeCode(routing.exceptionReason)}`
              ) : (
                "Routed."
              )}
            </p>
            {routing.trace.length > 0 && (
              <ol className="trace">
                {routing.trace.map((t) => (
                  <li key={t.ruleId} className={`trace__item${t.matched ? " trace__item--match" : ""}`}>
                    <span className="trace__icon">{t.matched ? <Icon name="check" size={14} /> : <Icon name="dot" size={6} />}</span>
                    <span className="trace__rule" title={t.ruleId}>{humanizeRuleName(t.ruleName)}</span>
                    {t.priority != null && <span className="trace__prio">p{t.priority}</span>}
                  </li>
                ))}
              </ol>
            )}
          </>
        ) : (
          <p className="muted">Not yet routed.</p>
        )}
      </section>
    </div>
  );
}
